import { injectable } from 'inversify'

/**
 * Wraps the system clock (invariant 8: all browser APIs behind a gateway) —
 * anything that reads "now" goes through this instead of `new Date()`
 * directly, so it can be frozen/advanced in tests.
 */
@injectable()
export class ClockGateway {
  now(): Date {
    return new Date()
  }

  /**
   * Repeating timer. Returns a disposer rather than a handle so callers never
   * hold a platform type, and stopping is the same call in test and production.
   *
   * Timers belong here for the same reason `now()` does: `setInterval` is
   * environment-coupled and nondeterministic, so a test must be able to drive
   * it rather than wait on it.
   */
  everyInterval(intervalMs: number, callback: () => void): () => void {
    const handle = setInterval(callback, intervalMs)
    return () => clearInterval(handle)
  }

  /** One-shot timer. Same contract as everyInterval — the disposer cancels it. */
  after(delayMs: number, callback: () => void): () => void {
    const handle = setTimeout(callback, delayMs)
    return () => clearTimeout(handle)
  }
}
